import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clapperboard, Music, BookOpen, Play, ExternalLink, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

class MediaErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="p-12 text-center bg-white/50 backdrop-blur-md rounded-[3rem] border-2 border-dashed border-magic/30">
                    <Sparkles className="w-16 h-16 text-magic/40 mx-auto mb-4" />
                    <h3 className="font-fredoka text-2xl font-bold text-foreground mb-2">Oops! The magic flickered.</h3>
                    <p className="font-nunito text-muted-foreground mb-6">We had a little trouble loading this part.</p>
                    <button
                        onClick={() => this.setState({ hasError: false })}
                        className="px-8 py-3 bg-magic text-white rounded-full font-fredoka font-bold shadow-hard hover:scale-105 transition-all"
                    >
                        Try Again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const VideoCard = ({ video }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white/80 backdrop-blur-md border-2 border-white/50 rounded-3xl overflow-hidden shadow-hard group hover:shadow-hard-lg transition-all"
    >
        <div className="relative aspect-video">
            <iframe
                src={`https://www.youtube-nocookie.com/embed/${video.id}`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
            />
        </div>
        <div className="p-4">
            <h3 className="font-fredoka font-semibold text-foreground line-clamp-1">{video.title}</h3>
            <p className="font-nunito text-xs text-muted-foreground mt-1">{video.category}</p>
        </div>
    </motion.div>
);

const AudioCard = ({ book, onPlay }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white/80 backdrop-blur-md border-2 border-white/50 rounded-3xl p-4 shadow-hard group hover:shadow-hard-lg transition-all flex items-center gap-4"
    >
        <div className="w-20 h-20 bg-magic/20 rounded-2xl overflow-hidden shrink-0 border border-white/50">
            {book.coverUrl ? (
                <img src={book.coverUrl} alt={book.title} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-magic">
                    <Music className="w-8 h-8" />
                </div>
            )}
        </div>
        <div className="flex-1 min-w-0">
            <h3 className="font-fredoka font-semibold text-foreground line-clamp-1">{book.title}</h3>
            <p className="font-nunito text-sm font-medium text-magic/80 mb-1">{book.author}</p>
            <p className="font-nunito text-xs text-muted-foreground line-clamp-2 leading-tight">
                {book.summary}
            </p>
        </div>
        <button
            onClick={() => onPlay(book)}
            className="p-4 bg-magic text-white rounded-full shadow-hard hover:scale-110 active:scale-95 transition-all shadow-magic/20"
        >
            <Play className="w-5 h-5 fill-current" />
        </button>
    </motion.div>
);

const StorybookCard = ({ book }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white/80 backdrop-blur-md border-2 border-white/50 rounded-3xl overflow-hidden shadow-hard group hover:shadow-hard-lg transition-all"
    >
        <div className="aspect-[3/4] relative">
            <img src={book.image} alt={book.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                    onClick={() => window.open(book.url, '_blank')}
                    className="px-4 py-2 bg-accent text-white rounded-full font-fredoka font-semibold flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-all"
                >
                    <ExternalLink className="w-4 h-4" />
                    View on Amazon
                </button>
            </div>
        </div>
        <div className="p-4 text-center">
            <h3 className="font-fredoka font-semibold text-foreground line-clamp-1">{book.title}</h3>
        </div>
    </motion.div>
);

const MediaPage = () => {
    const [activeTab, setActiveTab] = useState("videos");

    const videos = [
        { id: "M7lc1UVf-VE", title: "Learning for Kids", category: "Education" },
        { id: "3y5id_lXmxw", title: "Story Time: The Brave Little Turtle", category: "Stories" },
        { id: "983h51fFvx0", title: "Animal Adventures in the Wild", category: "Nature" },
        { id: "ovVp1NfIBIE", title: "Sesame Street: Letter of the Day", category: "Education" },
        { id: "dUpOq79GpeY", title: "National Geographic Kids: Cool Animals", category: "Nature" },
        { id: "PkZNo7MFNFg", title: "Peppa Pig Official", category: "Entertainment" },
        { id: "WRV47XoR4p0", title: "Alphablocks: Learn to Read", category: "Education" },
        { id: "h6m8vS5A2_w", title: "Cosmic Kids Yoga", category: "Activity" },
        { id: "jYI2fNnC0S4", title: "Art for Kids Hub: How to Draw", category: "Art" },
        { id: "l9n6X7-b-0", title: "Science for Kids: The Solar System", category: "Science" },
    ];

    const storybooks = [
        { title: "The Very Hungry Caterpillar", image: "https://m.media-amazon.com/images/I/91S+nAbS67L.jpg", url: "https://www.amazon.com/Very-Hungry-Caterpillar-Eric-Carle/dp/0399226907" },
        { title: "Goodnight Moon", image: "https://m.media-amazon.com/images/I/91pMv8M5D+L.jpg", url: "https://www.amazon.com/Goodnight-Moon-Margaret-Wise-Brown/dp/0060775858" },
        { title: "Don't Let the Pigeon Drive the Bus!", image: "https://m.media-amazon.com/images/I/71R2oEUnu2L._AC_UF1000,1000_QL80_.jpg", url: "https://www.amazon.com/Dont-Let-Pigeon-Drive-Bus/dp/078681988X" },
        { title: "Where the Wild Things Are", image: "https://m.media-amazon.com/images/I/91OonS8e7ML.jpg", url: "https://www.amazon.com/Where-Wild-Things-Maurice-Sendak/dp/0060254920" },
        { title: "Dragons Love Tacos", image: "https://m.media-amazon.com/images/I/91v0i+6IscL.jpg", url: "https://www.amazon.com/Dragons-Love-Tacos-Adam-Rubin/dp/0803736800" },
        { title: "The Day the Crayons Quit", image: "https://m.media-amazon.com/images/I/81S7G1z2vDL._AC_UF1000,1000_QL80_.jpg", url: "https://www.amazon.com/Day-Crayons-Quit-Drew-Daywalt/dp/0399255370" },
        { title: "If You Give a Mouse a Cookie", image: "https://m.media-amazon.com/images/I/91C9iXfS90L.jpg", url: "https://www.amazon.com/If-You-Give-Mouse-Cookie/dp/0060245867" },
        { title: "The Gruffalo", image: "https://m.media-amazon.com/images/I/91yN1Y9+I5L.jpg", url: "https://www.amazon.com/Gruffalo-Julia-Donaldson/dp/0142403873" },
    ];

    const [audiobooks, setAudiobooks] = useState([]);
    const [selectedAudio, setSelectedAudio] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAudiobooks = async () => {
            try {
                const response = await fetch('/data/audiobooks.catalog.json');
                if (!response.ok) throw new Error('Failed to load audiobooks');
                const data = await response.json();
                setAudiobooks(data);
            } catch (error) {
                console.error('Error loading audiobooks:', error);
                toast.error("Couldn't load audiobooks, using fallback list.");
                // Fallback to static list if fetch fails
                setAudiobooks([
                    { title: "Alice's Adventures in Wonderland", author: "Lewis Carroll", id: "alice_in_wonderland_librivox", summary: "A curious girl falls down a rabbit hole into a magical world." },
                    { title: "Peter Pan", author: "J. M. Barrie", id: "peter_pan_0707_librivox", summary: "The boy who wouldn't grow up takes the Darling children to Neverland." }
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchAudiobooks();
    }, []);

    const handlePlayAudio = (book) => {
        setSelectedAudio(book);
    };

    return (
        <div className="min-h-screen px-6 py-8 md:px-12">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-12"
            >
                <h1 className="font-fredoka text-5xl md:text-6xl font-bold text-foreground mb-4">
                    Sparkle Media Room 🎬
                </h1>
                <p className="font-nunito text-xl text-muted-foreground">
                    Discover movies, audiobooks, and storybooks!
                </p>
            </motion.div>

            {/* Tabs */}
            <div className="flex justify-center gap-4 mb-12 flex-wrap">
                {[
                    { id: "videos", label: "Movies & Shows", icon: Clapperboard, color: "bg-primary" },
                    { id: "audio", label: "Audiobooks", icon: Music, color: "bg-magic" },
                    { id: "stories", label: "Storybooks", icon: BookOpen, color: "bg-accent" }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-8 py-4 rounded-full font-fredoka font-bold text-lg flex items-center gap-3 transition-all ${activeTab === tab.id
                            ? `${tab.color} text-white shadow-hard-lg scale-105`
                            : "bg-white/80 text-foreground border-2 border-border hover:bg-muted"
                            }`}
                    >
                        <tab.icon className="w-6 h-6" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content Grid */}
            <div className="max-w-7xl mx-auto">
                <MediaErrorBoundary>
                    <AnimatePresence mode="wait">
                        {activeTab === "videos" && (
                            <motion.div
                                key="videos"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                            >
                                {videos.map(v => <VideoCard key={v.id} video={v} />)}
                            </motion.div>
                        )}

                        {activeTab === "audio" && (
                            <motion.div
                                key="audio"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                            >
                                {loading ? (
                                    <div className="col-span-full py-20 text-center">
                                        <Sparkles className="w-12 h-12 text-magic mx-auto mb-4 animate-spin-slow" />
                                        <p className="font-fredoka text-xl text-muted-foreground">Finding magical stories...</p>
                                    </div>
                                ) : (
                                    audiobooks.map((b, i) => <AudioCard key={i} book={b} onPlay={handlePlayAudio} />)
                                )}
                            </motion.div>
                        )}

                        {activeTab === "stories" && (
                            <motion.div
                                key="stories"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8"
                            >
                                {storybooks.map((b, i) => <StorybookCard key={i} book={b} />)}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </MediaErrorBoundary>
            </div>

            {/* Audio Player Modal */}
            <AnimatePresence>
                {selectedAudio && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/60 backdrop-blur-sm"
                        onClick={() => setSelectedAudio(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="bg-white rounded-[3rem] shadow-hard-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col relative"
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setSelectedAudio(null)}
                                className="absolute top-6 right-6 p-3 bg-muted hover:bg-muted/80 rounded-full transition-colors z-10"
                            >
                                <X className="w-6 h-6" />
                            </button>

                            <div className="p-8 pb-4">
                                <h2 className="font-fredoka text-3xl font-bold text-foreground mb-1 pr-12">
                                    {selectedAudio.title}
                                </h2>
                                <p className="font-nunito text-lg font-medium text-magic">
                                    {selectedAudio.author}
                                </p>
                            </div>

                            <div className="flex-1 overflow-hidden relative min-h-[400px] bg-muted/30">
                                <iframe
                                    src={`${selectedAudio.embedUrl}?playlist=1`}
                                    className="w-full h-full border-0"
                                    title={`Audiobook: ${selectedAudio.title}`}
                                    allow="autoplay"
                                    sandbox="allow-scripts allow-same-origin allow-popups"
                                />
                            </div>

                            <div className="p-8 bg-muted/10">
                                <p className="font-nunito text-muted-foreground leading-relaxed">
                                    {selectedAudio.summary}
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MediaPage;
